// Canonical Horse Racing physical-appearance contract.
//
// Appearance belongs to a horse identity, never to its gate. Race-slot
// accents (saddlecloth, tote chips, labels, and light trails) live in
// odds.js as GATE_ACCENT_* and must not be used to tint the horse body.

export const HORSE_APPEARANCE_VERSION = 1;
export const HORSE_APPEARANCE_DOMAIN = 'horseracing-horse-appearance:v1';

export const HORSE_APPEARANCE_SCHEMA = Object.freeze({
  version: HORSE_APPEARANCE_VERSION,
  colorFormat: '#rrggbb',
  markings: Object.freeze(['none', 'star', 'blaze', 'snip', 'stockings']),
  modelVariants: Object.freeze(['thoroughbred']),
  profileVariants: Object.freeze(['horse-f']),
});

/**
 * @typedef {Object} HorseAppearanceV1
 * @property {1} version Serialized contract version.
 * @property {string} coat Lowercase, six-digit sRGB hex (`#rrggbb`).
 * @property {string} mane Lowercase, six-digit sRGB hex (`#rrggbb`).
 * @property {string} tail Lowercase, six-digit sRGB hex (`#rrggbb`).
 * @property {'none'|'star'|'blaze'|'snip'|'stockings'} markings
 * @property {'thoroughbred'} modelVariant Semantic model family, not an asset URL.
 * @property {'horse-f'} profileVariant Semantic profile-art family, not an asset URL.
 */

const COLOR_RE = /^#[0-9a-fA-F]{6}$/;
const MARKINGS = new Set(HORSE_APPEARANCE_SCHEMA.markings);
const MODEL_VARIANTS = new Set(HORSE_APPEARANCE_SCHEMA.modelVariants);
const PROFILE_VARIANTS = new Set(HORSE_APPEARANCE_SCHEMA.profileVariants);
const textEncoder = new TextEncoder();

// Realistic base combinations. Identity selects one preset; field composition
// never edits a horse merely to avoid a same-coat matchup.
const COAT_PRESETS = Object.freeze([
  ['#6b3826', '#211815', '#211815'],
  ['#542a22', '#1d1615', '#1d1615'],
  ['#422923', '#181514', '#181514'],
  ['#342622', '#171515', '#171515'],
  ['#242122', '#161515', '#161515'],
  ['#8a4328', '#4a241c', '#4a241c'],
  ['#a6532d', '#64301f', '#64301f'],
  ['#b76735', '#d8b47c', '#d8b47c'],
  ['#c09352', '#ead9a9', '#ead9a9'],
  ['#a7834f', '#2c2520', '#2c2520'],
  ['#8d744f', '#302922', '#302922'],
  ['#a66f4e', '#5a382b', '#5a382b'],
  ['#68645d', '#292725', '#292725'],
  ['#999892', '#55534f', '#55534f'],
  ['#737579', '#3d3e40', '#3d3e40'],
  ['#b1a69d', '#716a65', '#716a65'],
  ['#c8c4b9', '#8f8b83', '#8f8b83'],
  ['#94685c', '#4b302a', '#4b302a'],
  ['#686b70', '#323438', '#323438'],
  ['#56382e', '#2a211e', '#2a211e'],
]);

function normalizeIdentityValue(value) {
  if (value == null) return '';
  if (typeof value === 'number' && !Number.isFinite(value)) return '';
  const text = String(value).normalize('NFKC').trim().replace(/\s+/gu, ' ');
  return text.slice(0, 256);
}

/**
 * Stable identity used only for legacy appearance fallback. Persistent horse
 * services should send `horseId` and a complete `appearance`; name remains the
 * compatibility identity for the current curated stable. Gate is deliberately
 * absent from this function.
 */
export function stableHorseIdentity(horse = {}) {
  for (const key of ['horseId', 'horse_id', 'id', 'name']) {
    const identity = normalizeIdentityValue(horse?.[key]);
    if (identity) return identity;
  }
  return 'anonymous-horse';
}

// FNV-1a over UTF-8: synchronous in render code and byte-identical to the
// backend implementation. It is a presentation selector, not fairness RNG.
export function appearanceHash32(value) {
  let hash = 0x811c9dc5;
  for (const byte of textEncoder.encode(String(value))) {
    hash ^= byte;
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash >>> 0;
}

function selector(identity, axis) {
  return appearanceHash32(`${HORSE_APPEARANCE_DOMAIN}\0${identity}\0${axis}`);
}

/**
 * Deterministic compatibility appearance for a stable horse identity.
 * This consumes no race RNG and does not depend on gate, field order, odds,
 * physics, or outcome.
 *
 * @returns {HorseAppearanceV1}
 */
export function deriveHorseAppearance(identity) {
  const stableIdentity = normalizeIdentityValue(identity) || 'anonymous-horse';
  const preset = COAT_PRESETS[selector(stableIdentity, 'coat') % COAT_PRESETS.length];
  const markings = HORSE_APPEARANCE_SCHEMA.markings[
    selector(stableIdentity, 'markings') % HORSE_APPEARANCE_SCHEMA.markings.length
  ];
  return {
    version: HORSE_APPEARANCE_VERSION,
    coat: preset[0],
    mane: preset[1],
    tail: preset[2],
    markings,
    modelVariant: 'thoroughbred',
    profileVariant: 'horse-f',
  };
}

function safeColor(value, fallback) {
  return typeof value === 'string' && COLOR_RE.test(value)
    ? value.toLowerCase()
    : fallback;
}

/**
 * Validate an untrusted serialized appearance and return one canonical v1
 * object. Invalid/missing fields fall back by horse identity; CSS functions,
 * named colors, alpha hex, URLs, and unknown variants are never accepted.
 *
 * @returns {HorseAppearanceV1}
 */
export function normalizeHorseAppearance(value, identity) {
  const fallback = deriveHorseAppearance(identity);
  if (!value || typeof value !== 'object' || Array.isArray(value)
      || value.version !== HORSE_APPEARANCE_VERSION) {
    return fallback;
  }
  return {
    version: HORSE_APPEARANCE_VERSION,
    coat: safeColor(value.coat, fallback.coat),
    mane: safeColor(value.mane, fallback.mane),
    tail: safeColor(value.tail, fallback.tail),
    markings: MARKINGS.has(value.markings) ? value.markings : fallback.markings,
    modelVariant: MODEL_VARIANTS.has(value.modelVariant)
      ? value.modelVariant
      : fallback.modelVariant,
    profileVariant: PROFILE_VARIANTS.has(value.profileVariant)
      ? value.profileVariant
      : fallback.profileVariant,
  };
}

/** Return the canonical appearance carried by one field-horse record. */
export function horseAppearance(horse = {}) {
  return normalizeHorseAppearance(horse.appearance, stableHorseIdentity(horse));
}

/** Normalize legacy/backend fields without mutating the serialized input. */
export function normalizeRaceField(field) {
  if (!field || !Array.isArray(field.horses)) return field;
  return {
    ...field,
    horses: field.horses.map((horse) => ({
      ...horse,
      appearance: horseAppearance(horse),
    })),
  };
}
